import { createElement } from "react";

import toast from "react-hot-toast";

import * as Sentry from "@sentry/react";
import { Button } from "semantic-ui-react";

import { API, APIError } from "simplydo/core/api";
import { WebsocketAPI } from "simplydo/core/api/websocket";
import { setOrgTimezone } from "simplydo/core/dates";
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from "simplydo/core/helpers";
import { i18n } from "simplydo/core/i18n";
import { asQuery, queryKey } from "simplydo/core/query";
import SearchParams from "simplydo/core/searchParams";
import { OpenAPI } from "simplydo/interfaces";

import actions from "actions";
import { store } from "store";
import util from "utils/utils";

export const websocketApi = new WebsocketAPI({
  websocketUrl: import.meta.env.VITE_WEBSOCKET_API_URL,
  generateToken: () =>
    new Promise((resolve) =>
      api.auth.requestWebsocketToken(
        ({ token: newWebsocketToken }) => resolve(newWebsocketToken),
        () => resolve(null),
      ),
    ),
});

const onLogout = () => {
  // @ts-ignore
  window.$chatwoot?.reset();
  websocketApi.disconnect();
  api.token = null;
  store.dispatch(actions.messages.logout());
  store.dispatch(actions.user.logout());
  store.dispatch(actions.auth.logout());
};

const api = new API({
  getApiUrl: () => {
    return import.meta.env.VITE_API_URL;
  },
  onBeforeRequest: (xhr) => {
    const gitSha = import.meta.env.VITE_GIT_SHA;
    if (gitSha) {
      xhr.setRequestHeader("SimplyDo-ClientVersion", gitSha);
    }

    const now = new Date();
    const { user } = store.getState();
    if (
      user?._id &&
      util.localStorageIsSupported() &&
      now.getTime() - new Date(getLocalStorageItem("heartbeat")!).getTime() >
        3600000
    ) {
      setLocalStorageItem("heartbeat", now.toString());
      api.users.heartbeat(
        user._id,
        () => {},
        () => {},
      );
    }
  },
  onLogin: (success: () => void, fail: (error: APIError) => void) => {
    if (!api.token) {
      const token = getLocalStorageItem("auth_token");
      if (token) {
        api.token = token;
      } else {
        fail?.({ status: 401, message: "No token found" });
        return;
      }
    }

    store.dispatch(actions.entry.clear());
    store.dispatch(actions.auth.login());
    store.dispatch(actions.auth.startGettingUser());

    const storeState = store.getState();

    const params = new SearchParams(window.location.search);
    const utmReferrer = params.get("utm-link") || storeState.auth.utmLink;

    // Wrap queryFn with timeout as safety measure
    // (authenticatedRequest now always calls callbacks, but timeout prevents infinite hangs)
    const queryFn = asQuery(api.users.me, [utmReferrer])!;
    const wrappedQueryFn = async () => {
      const timeoutPromise = new Promise<OpenAPI.GET<"/users/me">["response"]>(
        (_, reject) => {
          setTimeout(
            () =>
              reject(
                new Error(
                  "User query timed out - authentication may have failed",
                ),
              ),
            30000, // 30 second timeout
          );
        },
      );
      return Promise.race([queryFn(), timeoutPromise]);
    };
    api.queryClient
      .fetchQuery({ queryKey: queryKey(api.users.me), queryFn: wrappedQueryFn })
      .then((user) => {
        websocketApi.connect();
        // After resuming a session we clear the link and challenge utms
        store.dispatch(actions.auth.setClearUtm(true));
        store.dispatch(actions.user.receiveUser(user));
        store.dispatch(actions.auth.finishGettingUser());
        const ideaViewPreference = getLocalStorageItem(
          "ideaViewTypePreference",
        );
        if (ideaViewPreference && ideaViewPreference !== "board") {
          store.dispatch(
            actions.challenges.setIdeaViewType(ideaViewPreference),
          );
        }

        const searchParams = new SearchParams(window.location.search);
        const paramLang = searchParams.get("lang");
        if (paramLang) {
          i18n.changeLanguage(paramLang);
          if (user.language && user.language !== paramLang) {
            toast(
              (t) =>
                createElement("div", { style: { fontSize: "0.9em" } }, [
                  "You followed a link with a different language preference than yours.",
                  createElement(
                    "div",
                    {
                      style: {
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: "1rem",
                      },
                    },
                    [
                      createElement(
                        Button,
                        {
                          size: "small",
                          onClick: () => {
                            i18n.changeLanguage(user.language);
                            toast.dismiss(t.id);
                          },
                        },
                        "Revert",
                      ),
                      createElement(
                        Button,
                        {
                          size: "small",
                          primary: true,
                          onClick: () => {
                            toast.dismiss(t.id);
                          },
                        },
                        "Keep",
                      ),
                    ],
                  ),
                ]),
              { duration: Infinity },
            );
          }
        } else if (user.language) {
          i18n.changeLanguage(user.language);
        } else {
          i18n.changeLanguage(user.ownerOrganisation?.defaultLanguage || "en");
        }
        setOrgTimezone(user.ownerOrganisation?.defaultTimezone);
        // @ts-ignore - Seems not typed correctly in @sentry/react
        Sentry.setUser({
          id: user._id,
          username: user.profile.fullName,
          organisation: user.ownerOrganisation.name,
          orgId: user.ownerOrganisation._id,
        });
        success?.();
      })
      .catch((err) => {
        fail?.(err);
        store.dispatch(actions.auth.getUserFail(err));
      });
  },
  onLogout: onLogout,
  onFailedRequest: (xhr, onFail) => {
    if (xhr.status === 530) {
      onFail?.({
        status: 530,
        message: "Access from this IP address is not allowed",
      });
      return;
    } else if (xhr.status === 401) {
      onLogout();
      onFail?.({ status: 401, message: "Session expired" });
      return;
    }
    try {
      const response = JSON.parse(xhr.responseText);
      onFail?.({
        ...response,
        status: xhr.status,
        message: response.message || xhr.statusText,
      });
    } catch {
      onFail?.({ status: xhr.status, message: xhr.statusText });
    }
  },
});

// Register query client globally to enable access to the React Query devtools
// @ts-ignore - React Query devtools
// window.__TANSTACK_QUERY_CLIENT__ = api.queryClient;

window.api = api;

export default api;
